import { useMutation, useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import type {
  AdminConfirmPaymentRequest,
  AdminCreateAdjustmentRequest,
  AdminCreatePayoutRequest,
  AdminEditPayoutRequest,
  AdminEventReportItem,
  AdminEventReportListResponse,
  AdminImpersonateRequest,
  AdminImpersonationResponse,
  AdminInviteRequest,
  AdminLiabilityResponse,
  AdminLoginResponse,
  AdminLookupItem,
  AdminMarkPayoutPaidRequest,
  AdminMe,
  AdminMemberListItem,
  AdminMemberListResponse,
  AdminOrderDetail,
  AdminOrderListResponse,
  AdminOrganizerDetail,
  AdminOrganizerListResponse,
  AdminOrganizerPaymentSettings,
  AdminOverview,
  AdminPaymentProviderItem,
  AdminPaymentQueryResponse,
  AdminPayoutListResponse,
  AdminPlatformLogResponse,
  AdminRejectPayoutRequest,
  AdminReportGroupBy,
  AdminSalesReportResponse,
  AdminTeamMember,
  AdminUpdateBuyerRequest,
  AdminUpdateCompanyNameRequest,
  AdminUpdateFeesRequest,
  AdminUpdateOrganizerContactRequest,
  AdminUpdateOrganizerPaymentSettingsRequest,
  ApiProblem,
  MemberRole,
  MemberStatus,
  OrderStatus,
  OrganizerStatus,
  PayoutStatus,
  PaymentProviderKey,
  PayoutSummary,
  PlatformAdminRole,
  RefundOrderRequest,
  UpdateBankAccountRequest,
  UpdateOrganizerLegalInfoRequest,
} from "@ticketnjoy/types";

import { API_BASE_URL, ApiError, apiClient } from "@/lib/api-client";

export const adminKeys = {
  me: ["admin", "me"] as const,
  overview: ["admin", "overview"] as const,
  organizers: (params: OrganizerListParams) => ["admin", "organizers", params] as const,
  organizer: (id: string) => ["admin", "organizer", id] as const,
  finance: (organizerId: string) => ["admin", "finance", organizerId] as const,
  payouts: (params: PayoutListParams) => ["admin", "payouts", params] as const,
  salesReport: (params: SalesReportParams) => ["admin", "reports", "sales", params] as const,
  liability: ["admin", "reports", "liability"] as const,
  eventReports: (organizerId: string, page: number) =>
    ["admin", "event-reports", organizerId, page] as const,
  team: ["admin", "team"] as const,
  members: (params: MemberListParams) => ["admin", "members", params] as const,
  orders: (params: OrderListParams) => ["admin", "orders", params] as const,
  order: (orderNumber: string) => ["admin", "order", orderNumber] as const,
  platformLogs: (params: PlatformLogParams) => ["admin", "platform-logs", params] as const,
  platformLogAdmins: ["admin", "platform-logs", "admins"] as const,
  paymentProviders: ["admin", "payment-providers"] as const,
  organizerPayment: (organizerId: string) => ["admin", "organizer-payment", organizerId] as const,
};

// ---------------- Auth ----------------

/**
 * Oturum yokken sekmeye dönüşte yeniden sorgulanmaz: sorgu sürerken guard spinner gösterip
 * login sayfasını söker, kod adımı ve yazılan alanlar kaybolur.
 */
const refetchOnlyWhenSignedIn = (query: { state: { data: unknown } }) =>
  query.state.data !== undefined;

export function useAdminMeQuery() {
  return useQuery({
    queryKey: adminKeys.me,
    queryFn: () => apiClient<AdminMe>("/admin/auth/me"),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: refetchOnlyWhenSignedIn,
    refetchOnReconnect: refetchOnlyWhenSignedIn,
  });
}

/** Backend yazma endpoint'lerinde AdminSuper policy'si var; bu sadece UI'ı gizler. */
export function useIsSuperAdmin(): boolean {
  const { data } = useAdminMeQuery();
  return data?.role === "SuperAdmin";
}

export function useAdminLoginMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { email: string; password: string }) =>
      apiClient<AdminLoginResponse>("/admin/auth/login", { method: "POST", body }),
    onSuccess: (res) => {
      if (res.me) qc.setQueryData(adminKeys.me, res.me);
    },
  });
}

export function useAdminVerifyMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { challengeToken: string; code: string }) =>
      apiClient<AdminMe>("/admin/auth/verify", { method: "POST", body }),
    onSuccess: (me) => qc.setQueryData(adminKeys.me, me),
  });
}

export function useAdminLogoutMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiClient<void>("/admin/auth/logout", { method: "POST" }),
    onSettled: () => {
      qc.clear();
    },
  });
}

export function useAdminForgotPasswordMutation() {
  return useMutation({
    mutationFn: (body: { email: string }) =>
      apiClient<void>("/admin/auth/forgot-password", { method: "POST", body }),
  });
}

export function useAdminResetPasswordMutation() {
  return useMutation({
    mutationFn: (body: { token: string; password: string }) =>
      apiClient<void>("/admin/auth/reset-password", { method: "POST", body }),
  });
}

// ---------------- Platform ----------------

export function useOverviewQuery() {
  return useQuery({
    queryKey: adminKeys.overview,
    queryFn: () => apiClient<AdminOverview>("/admin/overview"),
  });
}

export interface OrganizerListParams {
  q: string;
  status: OrganizerStatus | "";
  page: number;
}

export const ORGANIZER_PAGE_SIZE = 25;

export function useOrganizersQuery(params: OrganizerListParams) {
  return useQuery({
    queryKey: adminKeys.organizers(params),
    queryFn: () => {
      const search = new URLSearchParams({
        page: String(params.page),
        pageSize: String(ORGANIZER_PAGE_SIZE),
      });
      if (params.q) search.set("q", params.q);
      if (params.status) search.set("status", params.status);
      return apiClient<AdminOrganizerListResponse>(`/admin/organizers?${search}`);
    },
    placeholderData: keepPreviousData,
  });
}

export function useOrganizerQuery(id: string) {
  return useQuery({
    queryKey: adminKeys.organizer(id),
    queryFn: () => apiClient<AdminOrganizerDetail>(`/admin/organizers/${id}`),
  });
}

function useOrganizerMutation<TBody, TResult = unknown>(
  id: string,
  request: (body: TBody) => Promise<TResult>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: request,
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: adminKeys.organizer(id) }),
        qc.invalidateQueries({ queryKey: ["admin", "organizers"] }),
        qc.invalidateQueries({ queryKey: adminKeys.overview }),
        qc.invalidateQueries({ queryKey: ["admin", "platform-logs"] }),
      ]);
    },
  });
}

export function useUpdateFeesMutation(id: string) {
  return useOrganizerMutation(id, (body: AdminUpdateFeesRequest) =>
    apiClient(`/admin/organizers/${id}/fees`, { method: "PUT", body }),
  );
}

export function useUpdateContactMutation(id: string) {
  return useOrganizerMutation(id, (body: AdminUpdateOrganizerContactRequest) =>
    apiClient<AdminOrganizerDetail>(`/admin/organizers/${id}/contact`, { method: "PUT", body }),
  );
}

export function useUpdateCompanyNameMutation(id: string) {
  return useOrganizerMutation(id, (body: AdminUpdateCompanyNameRequest) =>
    apiClient<AdminOrganizerDetail>(`/admin/organizers/${id}/company-name`, { method: "PUT", body }),
  );
}

export function useUpdateLegalInfoMutation(id: string) {
  return useOrganizerMutation(id, (body: UpdateOrganizerLegalInfoRequest) =>
    apiClient<AdminOrganizerDetail>(`/admin/organizers/${id}/legal-info`, { method: "PUT", body }),
  );
}

/** `file` null ise logo kaldırılır. */
export function useOrganizerLogoMutation(id: string) {
  return useOrganizerMutation(id, (file: File | null) => {
    if (!file) {
      return apiClient<AdminOrganizerDetail>(`/admin/organizers/${id}/logo`, { method: "DELETE" });
    }
    const form = new FormData();
    form.append("file", file);
    return apiClient<AdminOrganizerDetail>(`/admin/organizers/${id}/logo`, { method: "POST", body: form });
  });
}

export function useSuspendMutation(id: string) {
  return useOrganizerMutation(id, (body: { reason: string }) =>
    apiClient(`/admin/organizers/${id}/suspend`, { method: "POST", body }),
  );
}

export function useUnsuspendMutation(id: string) {
  return useOrganizerMutation<void>(id, () =>
    apiClient(`/admin/organizers/${id}/unsuspend`, { method: "POST" }),
  );
}

/** Yanıtla birlikte bu tarayıcıya organizatör oturum çerezi yazılır (mevcut dashboard oturumunu ezer). */
export function useImpersonateMutation(id: string) {
  return useOrganizerMutation(id, (body: AdminImpersonateRequest) =>
    apiClient<AdminImpersonationResponse>(`/admin/organizers/${id}/impersonate`, {
      method: "POST",
      body,
    }),
  );
}

export const EVENT_REPORT_PAGE_SIZE = 10;

export function useEventReportsQuery(organizerId: string, page: number) {
  return useQuery({
    queryKey: adminKeys.eventReports(organizerId, page),
    queryFn: () =>
      apiClient<AdminEventReportListResponse>(
        `/admin/organizers/${organizerId}/event-reports?page=${page}&pageSize=${EVENT_REPORT_PAGE_SIZE}`,
      ),
    placeholderData: keepPreviousData,
  });
}

export function useRegenerateEventReportMutation(organizerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (eventId: string) =>
      apiClient<AdminEventReportItem>(
        `/admin/organizers/${organizerId}/events/${eventId}/report/regenerate`,
        { method: "POST" },
      ),
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["admin", "event-reports", organizerId] }),
        qc.invalidateQueries({ queryKey: adminKeys.organizer(organizerId) }),
        qc.invalidateQueries({ queryKey: ["admin", "platform-logs"] }),
      ]);
    },
  });
}

// ---------------- Orders ----------------

export interface OrderListParams {
  q: string;
  status: OrderStatus | "";
  provider: string;
  /** YYYY-MM-DD, Türkiye günü, dahil. */
  from: string;
  to: string;
  organizerId: string;
  eventId: string;
  /** Yalnız Sorunlu ödemeler. */
  problem: boolean;
  page: number;
}

export const ORDER_PAGE_SIZE = 25;

export function useOrdersQuery(params: OrderListParams) {
  return useQuery({
    queryKey: adminKeys.orders(params),
    queryFn: () => {
      const search = new URLSearchParams({
        page: String(params.page),
        pageSize: String(ORDER_PAGE_SIZE),
      });
      if (params.q) search.set("q", params.q);
      if (params.status) search.set("status", params.status);
      if (params.provider) search.set("provider", params.provider);
      if (params.from) search.set("from", params.from);
      if (params.to) search.set("to", params.to);
      if (params.organizerId) search.set("organizerId", params.organizerId);
      if (params.eventId) search.set("eventId", params.eventId);
      if (params.problem) search.set("problem", "true");
      return apiClient<AdminOrderListResponse>(`/admin/orders?${search}`);
    },
    placeholderData: keepPreviousData,
  });
}

export function useOrderQuery(orderNumber: string) {
  return useQuery({
    queryKey: adminKeys.order(orderNumber),
    queryFn: () => apiClient<AdminOrderDetail>(`/admin/orders/${encodeURIComponent(orderNumber)}`),
  });
}

/** Detay yanıtı dönen işlemler önbelleği doğrudan günceller; liste ve platform kayıtları tazelenir. */
function useOrderMutation<TVars, TResult = AdminOrderDetail>(
  orderNumber: string,
  request: (vars: TVars) => Promise<TResult>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: request,
    onSuccess: async (result) => {
      if (result) qc.setQueryData(adminKeys.order(orderNumber), result);
      else await qc.invalidateQueries({ queryKey: adminKeys.order(orderNumber) });
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["admin", "orders"] }),
        qc.invalidateQueries({ queryKey: ["admin", "platform-logs"] }),
      ]);
    },
  });
}

function orderPath(orderNumber: string, action: string): string {
  return `/admin/orders/${encodeURIComponent(orderNumber)}/${action}`;
}

export function useResendOrderNotificationMutation(orderNumber: string) {
  return useOrderMutation(orderNumber, (channel: "email" | "sms") =>
    apiClient<void>(orderPath(orderNumber, `resend-${channel}`), { method: "POST" }),
  );
}

export function useUpdateOrderBuyerMutation(orderNumber: string) {
  return useOrderMutation(orderNumber, (body: AdminUpdateBuyerRequest) =>
    apiClient<AdminOrderDetail>(orderPath(orderNumber, "buyer"), { method: "PUT", body }),
  );
}

export function useRefundOrderMutation(orderNumber: string) {
  return useOrderMutation(orderNumber, (body: RefundOrderRequest) =>
    apiClient<AdminOrderDetail>(orderPath(orderNumber, "refund"), { method: "POST", body }),
  );
}

export function useCancelOrderMutation(orderNumber: string) {
  return useOrderMutation(orderNumber, (body: { reason: string }) =>
    apiClient<AdminOrderDetail>(orderPath(orderNumber, "cancel"), { method: "POST", body }),
  );
}

/** Sorgu sonucu yalnız ekranda tutulur; platform kaydı tazelenir. */
export function usePaymentQueryMutation(orderNumber: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiClient<AdminPaymentQueryResponse>(orderPath(orderNumber, "payment-query"), { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "platform-logs"] }),
  });
}

export function useConfirmPaymentMutation(orderNumber: string) {
  return useOrderMutation(orderNumber, (body: AdminConfirmPaymentRequest) =>
    apiClient<AdminOrderDetail>(orderPath(orderNumber, "confirm-payment"), { method: "POST", body }),
  );
}

export function useRefundPaymentMutation(orderNumber: string) {
  return useOrderMutation(orderNumber, (body: { reason: string }) =>
    apiClient<AdminOrderDetail>(orderPath(orderNumber, "refund-payment"), { method: "POST", body }),
  );
}

export function useDismissPaymentIssueMutation(orderNumber: string) {
  return useOrderMutation(orderNumber, (body: { note: string }) =>
    apiClient<AdminOrderDetail>(orderPath(orderNumber, "dismiss-payment-issue"), { method: "POST", body }),
  );
}

// ---------------- Organizer members ----------------

export interface MemberListParams {
  q: string;
  role: MemberRole | "";
  status: MemberStatus | "";
  organizerId: string;
  page: number;
  pageSize: number;
}

export const MEMBER_PAGE_SIZE = 25;

export function useMembersQuery(params: MemberListParams) {
  return useQuery({
    queryKey: adminKeys.members(params),
    queryFn: () => {
      const search = new URLSearchParams({
        page: String(params.page),
        pageSize: String(params.pageSize),
      });
      if (params.q) search.set("q", params.q);
      if (params.role) search.set("role", params.role);
      if (params.status) search.set("status", params.status);
      if (params.organizerId) search.set("organizerId", params.organizerId);
      return apiClient<AdminMemberListResponse>(`/admin/members?${search}`);
    },
    placeholderData: keepPreviousData,
  });
}

export type MemberAction =
  | {
      kind:
        | "send-password-reset"
        | "resend-invite"
        | "verify-email"
        | "disable"
        | "enable"
        | "revoke-sessions"
        | "make-owner";
      member: AdminMemberListItem;
    }
  | { kind: "email"; member: AdminMemberListItem; email: string }
  | { kind: "role"; member: AdminMemberListItem; role: MemberRole };

/** E-posta / sahiplik değişikliği organizatör detayındaki hesap e-postasını da değiştirir. */
export function useMemberActionMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (action: MemberAction) => {
      const path = `/admin/members/${action.member.id}/${action.kind}`;
      switch (action.kind) {
        case "email":
          return apiClient<AdminMemberListItem>(path, { method: "PUT", body: { email: action.email } });
        case "role":
          return apiClient<AdminMemberListItem>(path, { method: "PUT", body: { role: action.role } });
        default:
          return apiClient<AdminMemberListItem>(path, { method: "POST" });
      }
    },
    onSettled: async (_result, _error, action) => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["admin", "members"] }),
        qc.invalidateQueries({ queryKey: ["admin", "platform-logs"] }),
        qc.invalidateQueries({ queryKey: adminKeys.organizer(action.member.organizerId) }),
        qc.invalidateQueries({ queryKey: ["admin", "organizers"] }),
      ]);
    },
  });
}

// ---------------- Team ----------------

export function useTeamQuery(enabled = true) {
  return useQuery({
    queryKey: adminKeys.team,
    queryFn: () => apiClient<AdminTeamMember[]>("/admin/team"),
    enabled,
  });
}

/** Davet maili gitmese de (502) kayıt oluşmuş olabilir; liste her durumda tazelenir. */
function useTeamMutation<TVars>(request: (vars: TVars) => Promise<AdminTeamMember>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: request,
    onSettled: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: adminKeys.team }),
        qc.invalidateQueries({ queryKey: ["admin", "platform-logs"] }),
      ]);
    },
  });
}

export function useInviteAdminMutation() {
  return useTeamMutation((body: AdminInviteRequest) =>
    apiClient<AdminTeamMember>("/admin/team", { method: "POST", body }),
  );
}

export function useResendInviteMutation() {
  return useTeamMutation((id: string) =>
    apiClient<AdminTeamMember>(`/admin/team/${id}/resend-invite`, { method: "POST" }),
  );
}

export function useChangeAdminRoleMutation() {
  return useTeamMutation(({ id, role }: { id: string; role: PlatformAdminRole }) =>
    apiClient<AdminTeamMember>(`/admin/team/${id}/role`, { method: "PUT", body: { role } }),
  );
}

export function useSetAdminStatusMutation() {
  return useTeamMutation(({ id, active }: { id: string; active: boolean }) =>
    apiClient<AdminTeamMember>(`/admin/team/${id}/${active ? "enable" : "disable"}`, {
      method: "POST",
    }),
  );
}

/** Kendi oturumları için çağrılırsa o anki oturum açık kalır. */
export function useRevokeAdminSessionsMutation() {
  return useTeamMutation((id: string) =>
    apiClient<AdminTeamMember>(`/admin/team/${id}/revoke-sessions`, { method: "POST" }),
  );
}

export interface PlatformLogParams {
  adminId: string;
  action: string;
  organizerId: string;
  page: number;
}

export const PLATFORM_LOG_PAGE_SIZE = 50;

export function usePlatformLogsQuery(params: PlatformLogParams) {
  return useQuery({
    queryKey: adminKeys.platformLogs(params),
    queryFn: () => {
      const search = new URLSearchParams({
        page: String(params.page),
        pageSize: String(PLATFORM_LOG_PAGE_SIZE),
      });
      if (params.adminId) search.set("adminId", params.adminId);
      if (params.action) search.set("action", params.action);
      if (params.organizerId) search.set("organizerId", params.organizerId);
      return apiClient<AdminPlatformLogResponse>(`/admin/audit-logs?${search}`);
    },
    placeholderData: keepPreviousData,
  });
}

/** Filtre listesi; ekip listesi SuperAdmin'e özel olduğu için ayrı endpoint. */
export function usePlatformLogAdminsQuery() {
  return useQuery({
    queryKey: adminKeys.platformLogAdmins,
    queryFn: () => apiClient<AdminLookupItem[]>("/admin/audit-logs/admins"),
    staleTime: 5 * 60 * 1000,
  });
}

// ---------------- Reports ----------------

export interface SalesReportParams {
  /** YYYY-MM-DD, Türkiye günü, dahil. */
  from: string;
  to: string;
  groupBy: AdminReportGroupBy;
}

function salesReportSearch(params: SalesReportParams): URLSearchParams {
  return new URLSearchParams({ from: params.from, to: params.to, groupBy: params.groupBy });
}

export function useSalesReportQuery(params: SalesReportParams, enabled = true) {
  return useQuery({
    queryKey: adminKeys.salesReport(params),
    queryFn: () =>
      apiClient<AdminSalesReportResponse>(`/admin/reports/sales?${salesReportSearch(params)}`),
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useLiabilityQuery() {
  return useQuery({
    queryKey: adminKeys.liability,
    queryFn: () => apiClient<AdminLiabilityResponse>("/admin/reports/liability"),
  });
}

export async function downloadSalesReportXlsx(params: SalesReportParams): Promise<void> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}/admin/reports/sales/export.xlsx?${salesReportSearch(params)}`, {
      credentials: "include",
      headers: { "X-Locale": "tr" },
    });
  } catch {
    throw new ApiError(0, "network_error", "Sunucuya ulaşılamadı.");
  }
  if (!response.ok) {
    const isJson = (response.headers.get("content-type") ?? "").includes("json");
    const problem = (isJson ? await response.json() : {}) as ApiProblem;
    throw new ApiError(
      response.status,
      problem.title ?? "unknown_error",
      problem.detail ?? "Excel indirilemedi.",
      problem.errors,
      problem.traceId,
    );
  }

  const link = document.createElement("a");
  link.href = URL.createObjectURL(await response.blob());
  link.download = `ticketnjoy-rapor-${params.from}-${params.to}.xlsx`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(link.href);
}

// ---------------- Payouts ----------------

/** `open` = onay bekleyen + EFT bekleyen. */
export type PayoutListFilter = "open" | PayoutStatus | "";

export interface PayoutListParams {
  status: PayoutListFilter;
  page: number;
}

export const PAYOUT_PAGE_SIZE = 25;

export function usePayoutsQuery(params: PayoutListParams) {
  return useQuery({
    queryKey: adminKeys.payouts(params),
    queryFn: () => {
      const search = new URLSearchParams({
        page: String(params.page),
        pageSize: String(PAYOUT_PAGE_SIZE),
      });
      if (params.status) search.set("status", params.status);
      return apiClient<AdminPayoutListResponse>(`/admin/payouts?${search}`);
    },
    placeholderData: keepPreviousData,
  });
}

export function useOrganizerFinanceQuery(organizerId: string) {
  return useQuery({
    queryKey: adminKeys.finance(organizerId),
    queryFn: () => apiClient<PayoutSummary>(`/admin/organizers/${organizerId}/finance`),
  });
}

/** Tüm cari mutation'ları güncel özeti döner; liste, özet ve platform işlemleri de tazelenir. */
function useFinanceMutation<TVars>(organizerId: string, request: (vars: TVars) => Promise<PayoutSummary>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: request,
    onSuccess: async (summary) => {
      qc.setQueryData(adminKeys.finance(organizerId), summary);
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["admin", "payouts"] }),
        qc.invalidateQueries({ queryKey: adminKeys.overview }),
        qc.invalidateQueries({ queryKey: adminKeys.organizer(organizerId) }),
        qc.invalidateQueries({ queryKey: ["admin", "platform-logs"] }),
      ]);
    },
  });
}

export function useCreatePayoutMutation(organizerId: string) {
  return useFinanceMutation(organizerId, (body: AdminCreatePayoutRequest) =>
    apiClient<PayoutSummary>(`/admin/organizers/${organizerId}/payouts`, { method: "POST", body }),
  );
}

export function useCreateAdjustmentMutation(organizerId: string) {
  return useFinanceMutation(organizerId, (body: AdminCreateAdjustmentRequest) =>
    apiClient<PayoutSummary>(`/admin/organizers/${organizerId}/adjustments`, { method: "POST", body }),
  );
}

/** Owner'a bildirim gitmez; bekleyen talepler yeni hesaba geçer. */
export function useUpdateBankAccountMutation(organizerId: string) {
  return useFinanceMutation(organizerId, (body: UpdateBankAccountRequest) =>
    apiClient<PayoutSummary>(`/admin/organizers/${organizerId}/bank-account`, { method: "PUT", body }),
  );
}

export function useEditPayoutMutation(organizerId: string) {
  return useFinanceMutation(
    organizerId,
    ({ id, approve, body }: { id: string; approve: boolean; body: AdminEditPayoutRequest }) =>
      apiClient<PayoutSummary>(approve ? `/admin/payouts/${id}/approve` : `/admin/payouts/${id}`, {
        method: approve ? "POST" : "PUT",
        body,
      }),
  );
}

export function useMarkPayoutPaidMutation(organizerId: string) {
  return useFinanceMutation(organizerId, ({ id, body }: { id: string; body: AdminMarkPayoutPaidRequest }) =>
    apiClient<PayoutSummary>(`/admin/payouts/${id}/mark-paid`, { method: "POST", body }),
  );
}

export function useRejectPayoutMutation(organizerId: string) {
  return useFinanceMutation(organizerId, ({ id, body }: { id: string; body: AdminRejectPayoutRequest }) =>
    apiClient<PayoutSummary>(`/admin/payouts/${id}/reject`, { method: "POST", body }),
  );
}

// ---------------- Payment settings ----------------

export function usePaymentProvidersQuery() {
  return useQuery({
    queryKey: adminKeys.paymentProviders,
    queryFn: () => apiClient<AdminPaymentProviderItem[]>("/admin/payment-providers"),
  });
}

/** Her işlem güncel listeyi döner; organizatörlerin geçerli POS'u da değişmiş olabilir. */
function usePaymentProviderMutation<TVars>(request: (vars: TVars) => Promise<AdminPaymentProviderItem[]>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: request,
    onSuccess: async (items) => {
      qc.setQueryData(adminKeys.paymentProviders, items);
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["admin", "organizer-payment"] }),
        qc.invalidateQueries({ queryKey: ["admin", "platform-logs"] }),
      ]);
    },
  });
}

export function useSetProviderAvailabilityMutation() {
  return usePaymentProviderMutation(
    ({ key, ...body }: { key: PaymentProviderKey; isAvailable: boolean; reason?: string | null }) =>
      apiClient<AdminPaymentProviderItem[]>(`/admin/payment-providers/${key}/availability`, {
        method: "PUT",
        body,
      }),
  );
}

export function useUpdateProviderCardFeeMutation() {
  return usePaymentProviderMutation(({ key, cardFeeRate }: { key: PaymentProviderKey; cardFeeRate: number }) =>
    apiClient<AdminPaymentProviderItem[]>(`/admin/payment-providers/${key}/card-fee`, {
      method: "PUT",
      body: { cardFeeRate },
    }),
  );
}

export function useMakeDefaultProviderMutation() {
  return usePaymentProviderMutation((key: PaymentProviderKey) =>
    apiClient<AdminPaymentProviderItem[]>(`/admin/payment-providers/${key}/make-default`, { method: "POST" }),
  );
}

/** Sonuç (başarılı ya da değil) sağlayıcının son test alanlarına yazılır. */
export function useTestProviderMutation() {
  return usePaymentProviderMutation((key: PaymentProviderKey) =>
    apiClient<AdminPaymentProviderItem[]>(`/admin/payment-providers/${key}/test`, { method: "POST" }),
  );
}

export function useOrganizerPaymentSettingsQuery(organizerId: string) {
  return useQuery({
    queryKey: adminKeys.organizerPayment(organizerId),
    queryFn: () => apiClient<AdminOrganizerPaymentSettings>(`/admin/organizers/${organizerId}/payment-settings`),
  });
}

export function useUpdateOrganizerPaymentSettingsMutation(organizerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AdminUpdateOrganizerPaymentSettingsRequest) =>
      apiClient<AdminOrganizerPaymentSettings>(`/admin/organizers/${organizerId}/payment-settings`, {
        method: "PUT",
        body,
      }),
    onSuccess: async (settings) => {
      qc.setQueryData(adminKeys.organizerPayment(organizerId), settings);
      await Promise.all([
        qc.invalidateQueries({ queryKey: adminKeys.paymentProviders }),
        qc.invalidateQueries({ queryKey: adminKeys.organizer(organizerId) }),
        qc.invalidateQueries({ queryKey: ["admin", "platform-logs"] }),
      ]);
    },
  });
}
